import React, { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import { Check, ExternalLink, Clock } from "lucide-react";
import { toast } from "sonner";

const TYPE_COLOR = {
  Content: { bg: "var(--aw-accent-soft)", fg: "var(--aw-accent-ink)" },
  PR: { bg: "#FFEFEA", fg: "#C84A2F" },
  Schema: { bg: "#E8EFFB", fg: "#4285F4" },
  Reddit: { bg: "#FFEFEA", fg: "#C84A2F" },
  Wikipedia: { bg: "#EFEAFA", fg: "#7B5BD9" },
  Partnership: { bg: "#FBEBE2", fg: "#D97757" },
};

const STATUS_NEXT = {
  pending: "in_progress",
  in_progress: "completed",
  completed: "pending",
};

const STATUS_LABEL = {
  pending: "Open",
  in_progress: "In progress",
  completed: "Done",
  dismissed: "Dismissed",
};

const EFFORT_LABEL = {
  XS: "XS · < 1h",
  S: "S · 1-3h",
  M: "M · half day",
  L: "L · 1-2 days",
};

export default function Recommendations() {
  const { current, projects, selectProject } = useProject();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    if (!current) return;
    setLoading(true);
    api
      .get(`/projects/${current.project_id}/recommendations`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [current]);

  const filtered = useMemo(() => {
    if (!data?.recommendations) return [];
    if (filter === "all") return data.recommendations;
    if (filter === "open")
      return data.recommendations.filter(
        (r) => r.status === "pending" || r.status === "in_progress"
      );
    return data.recommendations.filter((r) => r.status === filter);
  }, [data, filter]);

  const stats = useMemo(() => {
    if (!data?.recommendations) return { totalLift: 0, count: 0, open: 0 };
    const open = data.recommendations.filter(
      (r) => r.status === "pending" || r.status === "in_progress"
    );
    const totalLift = open.reduce((acc, r) => acc + (r.est_lift || 0), 0);
    return { totalLift, count: data.recommendations.length, open: open.length };
  }, [data]);

  const toggleStatus = async (rec) => {
    if (!rec.action_id) {
      toast.info("This is a seeded recommendation — run a content audit to create real actions.");
      return;
    }
    const next = STATUS_NEXT[rec.status] || "pending";
    setUpdating(rec.id);
    try {
      await api.patch(`/recommendations/${rec.action_id}/status`, { status: next });
      setData((prev) => ({
        ...prev,
        recommendations: prev.recommendations.map((r) =>
          r.id === rec.id ? { ...r, status: next } : r
        ),
      }));
      toast.success(`Marked as ${STATUS_LABEL[next]}`);
    } catch (e) {
      toast.error("Could not update status");
    } finally {
      setUpdating(null);
    }
  };

  const pushTo = (rec, tool) => {
    toast.info(`Pushed to ${tool}`, {
      description: `"${rec.title}" added as a ticket. (Demo — integrations land in Phase 3.)`,
    });
  };

  if (!current) {
    return (
      <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
        <div className="p-8 text-sm" style={{ color: "var(--aw-muted)" }}>
          No project selected.
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div data-testid="recommendations-page">
        <div className="mb-8">
          <div className="eyebrow">Weekly fix list</div>
          <h1
            className="mt-2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 40,
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
            }}
          >
            Recommendations
          </h1>
          <p className="mt-3 text-sm" style={{ color: "var(--aw-muted)", maxWidth: 560 }}>
            Specific things to ship — ranked by estimated lift per unit of
            effort. Updated every Monday from the week's scan results.
          </p>
        </div>

        {data?.is_seed && (
          <div
            className="mb-6 px-4 py-3 rounded-[10px] text-xs flex items-center justify-between"
            style={{ background: "var(--aw-accent-soft)", color: "var(--aw-accent-ink)" }}
            data-testid="recommendations-seed-banner"
          >
            <span>
              Showing representative recommendations. Run a content audit to
              generate real ones.
            </span>
            <a href="/dashboard/optimizer" className="underline">
              Run audit →
            </a>
          </div>
        )}

        {/* Summary strip */}
        {data && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="card-aw" data-testid="recommendations-stat-open">
              <div className="eyebrow">Open</div>
              <div
                className="mt-2"
                style={{ fontFamily: "var(--font-display)", fontSize: 32, lineHeight: 1 }}
              >
                {stats.open}
              </div>
            </div>
            <div className="card-aw" data-testid="recommendations-stat-lift">
              <div className="eyebrow">Total potential lift</div>
              <div
                className="mt-2"
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 32,
                  lineHeight: 1,
                  color: "var(--aw-accent-ink)",
                }}
              >
                +{stats.totalLift} pts
              </div>
            </div>
            <div className="card-aw" data-testid="recommendations-stat-total">
              <div className="eyebrow">All time</div>
              <div
                className="mt-2"
                style={{ fontFamily: "var(--font-display)", fontSize: 32, lineHeight: 1 }}
              >
                {stats.count}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        {data && (
          <div className="mb-5 flex items-center gap-2 flex-wrap" data-testid="recommendations-filters">
            {[
              ["open", "Open"],
              ["all", "All"],
              ["completed", "Done"],
              ["dismissed", "Dismissed"],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`pill ${filter === k ? "pill-accent" : ""}`}
                style={{ cursor: "pointer", height: 28 }}
                data-testid={`recommendations-filter-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="card-aw p-8 text-center text-sm" style={{ color: "var(--aw-muted)" }}>
            Loading…
          </div>
        ) : !data ? (
          <div className="card-aw p-8 text-center text-sm" style={{ color: "var(--aw-muted)" }}>
            No data.
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="card-aw p-10 text-center text-sm"
            style={{ color: "var(--aw-muted)" }}
            data-testid="recommendations-empty"
          >
            No recommendations match this filter. {filter === "open" && "Nice work."}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {filtered.map((rec) => {
              const tColor = TYPE_COLOR[rec.type_tag] || TYPE_COLOR.Content;
              const isDone = rec.status === "completed";
              return (
                <div
                  key={rec.id}
                  className="card-aw"
                  style={{ padding: 20, opacity: isDone ? 0.55 : 1 }}
                  data-testid={`recommendation-${rec.id}`}
                >
                  <div className="flex items-start gap-4">
                    <button
                      onClick={() => toggleStatus(rec)}
                      disabled={updating === rec.id}
                      data-testid={`recommendation-${rec.id}-status`}
                      title={`Click to mark as ${STATUS_LABEL[STATUS_NEXT[rec.status]] || "pending"}`}
                      style={{
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        borderRadius: 999,
                        border: `1px solid ${isDone ? "var(--aw-accent)" : "var(--aw-border-2)"}`,
                        background: isDone ? "var(--aw-accent)" : "var(--aw-surface)",
                        color: isDone ? "white" : "var(--aw-muted-2)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: 2,
                      }}
                    >
                      {isDone ? (
                        <Check size={14} />
                      ) : rec.status === "in_progress" ? (
                        <Clock size={14} style={{ color: "var(--aw-accent-ink)" }} />
                      ) : null}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3 flex-wrap">
                        <h3
                          style={{
                            fontFamily: "var(--font-body)",
                            fontSize: 16,
                            fontWeight: 500,
                            color: "var(--aw-ink)",
                            textDecoration: isDone ? "line-through" : "none",
                            margin: 0,
                            flex: 1,
                          }}
                        >
                          {rec.title}
                        </h3>
                        <span
                          className="mono"
                          style={{
                            fontSize: 13,
                            color: "var(--aw-accent-ink)",
                            fontWeight: 600,
                          }}
                        >
                          ↗ +{rec.est_lift}
                        </span>
                      </div>
                      <p
                        className="mt-1 text-sm"
                        style={{ color: "var(--aw-muted)", lineHeight: 1.5 }}
                      >
                        {rec.summary}
                      </p>
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <span
                          className="pill"
                          style={{
                            background: tColor.bg,
                            color: tColor.fg,
                            borderColor: "transparent",
                            fontSize: 11,
                            height: 22,
                          }}
                        >
                          {rec.type_tag}
                        </span>
                        <span
                          className="pill"
                          style={{ fontSize: 11, height: 22 }}
                          data-testid={`recommendation-${rec.id}-effort`}
                        >
                          {EFFORT_LABEL[rec.effort] || rec.effort}
                        </span>
                        <span
                          className="pill"
                          style={{ fontSize: 11, height: 22 }}
                        >
                          {rec.owner}
                        </span>
                        <span
                          className="pill"
                          style={{
                            fontSize: 11,
                            height: 22,
                            background:
                              rec.status === "completed"
                                ? "var(--aw-accent-soft)"
                                : rec.status === "in_progress"
                                ? "#FAEFD8"
                                : "var(--aw-surface-2)",
                            color:
                              rec.status === "completed"
                                ? "var(--aw-accent-ink)"
                                : rec.status === "in_progress"
                                ? "#B97A2B"
                                : "var(--aw-muted)",
                            borderColor: "transparent",
                          }}
                        >
                          {STATUS_LABEL[rec.status] || rec.status}
                        </span>
                        {rec.page_url && (
                          <a
                            href={rec.page_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs flex items-center gap-1"
                            style={{ color: "var(--aw-muted)" }}
                          >
                            <ExternalLink size={11} /> {rec.page_url.replace(/^https?:\/\//, "")}
                          </a>
                        )}
                        <div className="flex-1" />
                        <button
                          onClick={() => pushTo(rec, "Linear")}
                          className="btn-aw btn-aw-sm btn-aw-ghost"
                          data-testid={`recommendation-${rec.id}-push-linear`}
                        >
                          Linear
                        </button>
                        <button
                          onClick={() => pushTo(rec, "Notion")}
                          className="btn-aw btn-aw-sm btn-aw-ghost"
                          data-testid={`recommendation-${rec.id}-push-notion`}
                        >
                          Notion
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
